import { useState, type ReactNode } from 'react';
import { useEditorStore, useMovieStore, useUIStore } from '../../../stores';
import { useGet } from '../../../hooks';
import { useTranslation, type MessageKey } from '../../../i18n';
import { Logo } from '../../Logo';
import { MovieSelector } from '../MovieSelector';
import './Toolbar.css';
import {
  ChatBubbleIcon,
  CaretDownIcon,
  TextIcon,
  BellIcon,
  DotFilledIcon,
} from '@radix-ui/react-icons';
import { LineSegmentIcon, PathIcon } from '@phosphor-icons/react';
import { Button, Tooltip } from '@radix-ui/themes';
import { ZoomTools } from './ZoomTools';
import { ShapeTools } from './ShapeTools';
import { ThreeDTools } from './ThreeDTools';
import { CharacterTools } from './CharacterTools';
import { EffectsTools } from './EffectsTools';
import { SelectionTools } from './SelectionTools';
import { ThemeToggle } from '../../ThemeToggle';
import { UserMenu } from './UserMenu';

export function Toolbar() {
  const { currentMovie } = useMovieStore();

  return (
    <header className="editor-toolbar">
      <div className="editor-toolbar-left">
        <AIChatButton />
        <Logo />
        <MovieSelection />
      </div>
      <div className="editor-toolbar-center">
        {currentMovie && (
          <div className="flex gap-2">
            <SelectionTools />
            <DrawingTools />
            <ShapeTools />
            <ThreeDTools />
            <CharacterTools />
            <EffectsTools />
            <ZoomTools />
          </div>
        )}
      </div>
      <div className="editor-toolbar-right">
        <ThemeToggle />
        <NotificationsMenu />
        <UserMenu />
      </div>
    </header>
  );
}

function MovieSelection() {
  const { t } = useTranslation();
  const { currentMovie } = useMovieStore();
  const { welcomeOpen } = useUIStore();
  const [isOpen, setIsOpen] = useState(false);

  if (welcomeOpen) {
    return null;
  }

  return (
    <div className="movie-selector mt-1 px-4 py-2">
      <Button
        variant="ghost"
        radius="none"
        size="3"
        className={`movie-selector-button ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="text-sm text-theme-secondary">
          {currentMovie?.name || t('toolbar.selectMovie')}
        </span>
        <CaretDownIcon aria-hidden="true" className="ml-2" />
      </Button>
      <MovieSelector isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </div>
  );
}

function AIChatButton() {
  const { t } = useTranslation();
  const { aiChatVisible, setAiChatVisible } = useEditorStore();

  return (
    <Button
      variant="surface"
      radius="none"
      size="3"
      className={`ai-chat-button ${aiChatVisible ? 'active' : ''}`}
      onClick={() => setAiChatVisible(!aiChatVisible)}
    >
      <ChatBubbleIcon aria-hidden="true" />
      <span>{t('toolbar.ai')}</span>
    </Button>
  );
}

function DrawingTools() {
  const { t } = useTranslation();
  const { currentTool, setTool } = useEditorStore();

  const drawingTools: { id: 'line' | 'curve' | 'text'; icon: ReactNode; titleKey: MessageKey }[] = [
    { id: 'line', icon: <LineSegmentIcon />, titleKey: 'toolbar.tools.line' },
    { id: 'curve', icon: <PathIcon />, titleKey: 'toolbar.tools.curve' },
    { id: 'text', icon: <TextIcon />, titleKey: 'toolbar.tools.text' },
  ];

  return (
    <div className="drawing-toolbar">
      <div className="drawing-toolbar-group">
        {drawingTools.map((tool) => (
          <Tooltip key={tool.id} content={t(tool.titleKey)}>
            <Button
              variant="soft"
              radius="none"
              size="3"
              onClick={() => setTool(tool.id)}
              className={currentTool === tool.id ? 'active' : ''}
            >
              {tool.icon}
            </Button>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}

interface NotificationResponse {
  notificationId: string;
  type: string;
  title: string;
  message: string;
  createdAt: string;
  isRead: boolean;
}

function NotificationsMenu() {
  const { t, locale } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { data: notificationsData, loading: notificationsLoading } =
    useGet<NotificationResponse[]>('/api/user/notifications');
  const notifications = notificationsData ?? [];
  const unreadCount = notifications.filter((notification) => !notification.isRead).length;

  const formatTimestamp = (value: string): string => {
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return '';
    return parsedDate.toLocaleString(locale);
  };

  return (
    <div className="notifications-menu-root">
      <Button
        variant="surface"
        radius="none"
        size="3"
        className={`notifications-menu-button ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('toolbar.notifications')}
      >
        <BellIcon aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="notifications-menu-badge" aria-hidden="true">
            {unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <>
          <div className="user-menu-overlay" onClick={() => setIsOpen(false)} />
          <div className="notifications-menu-dropdown">
            <div className="notifications-menu-header">{t('toolbar.notifications')}</div>
            <div className="notifications-menu-list">
              {notificationsLoading && (
                <div className="notifications-menu-empty-state">
                  {t('toolbar.loadingNotifications')}
                </div>
              )}
              {!notificationsLoading && notifications.length === 0 && (
                <div className="notifications-menu-empty-state">
                  {t('toolbar.noNotifications')}
                </div>
              )}
              {!notificationsLoading &&
                notifications.map((notification) => (
                  <button
                    key={notification.notificationId}
                    type="button"
                    className="notifications-menu-item"
                  >
                    <div className="notifications-menu-item-title">
                      <DotFilledIcon
                        aria-hidden="true"
                        className={
                          notification.isRead ? 'notification-dot-read' : 'notification-dot-unread'
                        }
                      />
                      <span>{notification.title}</span>
                    </div>
                    <div className="notifications-menu-item-description">
                      {notification.message}
                    </div>
                    <div className="notifications-menu-item-time">
                      {formatTimestamp(notification.createdAt)}
                    </div>
                  </button>
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
